﻿import React, { useCallback, ChangeEvent } from 'react';
import moment from 'moment';
import DateTime from 'react-datetime';
import { FormControl } from 'react-bootstrap';
import { Translate } from '../../../../../shared/LanguageService';
import FormSelect from '../../../../../components/common/FormSelect';
import { IOrderType } from '../../Entities';
import { TranslatedDynamicLabels, TranslatedLabels } from '../../../../../shared/types';
import {
    validateEndDateInAvailableDateRange,
    validateStartDateInAvailableDateRange
} from './utils/orderDateUtils';

export type OrderTypeFilterOptions = {
    availableOrderTypes: IOrderType[];
    orderType: number;
    onChangeOrderType: (event: ChangeEvent<HTMLSelectElement>) => void;
};

type BrokerAgent = {
    id: number;
    name: string;
};

export type BrokerAgentFilterOptions = {
    availableBrokerAgents: BrokerAgent[];
    brokerAgent: number;
    onChangeBrokerAgent: (event: ChangeEvent<HTMLSelectElement>) => void;
};

export type FreeTextFilterOptions = {
    searchPhrase: string;
    onChangeFreeText: (event: ChangeEvent<HTMLInputElement>) => void;
};

export type OrderPlacedAtFilterOptions = {
    earliestAvailableDate: Date;
    orderPlacedAtStart: Date | null;
    orderPlacedAtEnd: Date | null;
    onOrderPlacedAtStartChange: (date: Date) => void;
    onOrderPlacedAtEndChange: (date: Date) => void;
};

type FilterOptionsProps = {
    orderTypeFilterOptions: OrderTypeFilterOptions;
    brokerAgentFilterOptions: BrokerAgentFilterOptions | null;
    freeTextFilterOptions: FreeTextFilterOptions;
    orderPlacedAtFilterOptions: OrderPlacedAtFilterOptions;
};

type TranslationProps = {
    labels: TranslatedLabels;
    dynamicLabels: TranslatedDynamicLabels;
};

function FilterOptions({
    orderTypeFilterOptions,
    brokerAgentFilterOptions,
    freeTextFilterOptions,
    orderPlacedAtFilterOptions,
    labels,
    dynamicLabels
}: FilterOptionsProps & TranslationProps) {
    const { availableOrderTypes, orderType, onChangeOrderType } = orderTypeFilterOptions;
    const { searchPhrase, onChangeFreeText } = freeTextFilterOptions;
    const {
        orderPlacedAtStart,
        orderPlacedAtEnd,
        onOrderPlacedAtStartChange,
        onOrderPlacedAtEndChange,
        earliestAvailableDate
    } = orderPlacedAtFilterOptions;

    const validateStartDate = useCallback(
        (date): boolean => {
            const actualDate = date.toDate(); // Because "date" is a moment date object.
            return validateStartDateInAvailableDateRange(actualDate, earliestAvailableDate);
        },
        [earliestAvailableDate]
    );

    const validateEndDate = useCallback(
        (date): boolean => {
            const actualDate = date.toDate(); // Because "date" is a moment date object.
            return validateEndDateInAvailableDateRange(
                actualDate,
                orderPlacedAtStart,
                earliestAvailableDate
            );
        },
        [orderPlacedAtStart]
    );

    return (
        <div className="filterOptions__container">
            <h1 className="filterOptions__heading col-xs-12">{labels.title}</h1>

            <div className="filterOptions__inputContainer">
                <div className="filterOptions__orderTypeContainer">
                    <label>{labels.orderTypeLabel}</label>
                    <FormSelect
                        autoFocus={true}
                        className="filterOptions__input"
                        value={orderType}
                        defaultOptionValue={-10}
                        defaultTranslateKey="orders/dropdown/order-type/default"
                        onChange={onChangeOrderType}
                    >
                        {availableOrderTypes.map((orderType) => {
                            return (
                                <option value={orderType.value} key={orderType.value}>
                                    {dynamicLabels.orderTypeOption(orderType.translateKey)}
                                </option>
                            );
                        })}
                    </FormSelect>
                </div>

                <div className="filterOptions__freeTextSearchContainer">
                    <label>{labels.searchLabel}</label>
                    <FormControl
                        type="text"
                        name="filter"
                        value={searchPhrase}
                        onChange={onChangeFreeText}
                        className="filterOptions__input"
                    />
                </div>

                {brokerAgentFilterOptions ? (
                    <div className="filterOptions__brokerAgentContainer">
                        <label>{labels.brokerAgentLabel}</label>
                        <FormSelect
                            value={brokerAgentFilterOptions.brokerAgent}
                            className="filterOptions__input"
                            defaultTranslateKey="orders/dropdown/broker-agent/default"
                            onChange={brokerAgentFilterOptions.onChangeBrokerAgent}
                        >
                            {brokerAgentFilterOptions.availableBrokerAgents.map((brokerAgent) => {
                                return (
                                    <option value={brokerAgent.id} key={brokerAgent.id}>
                                        {brokerAgent.name}
                                    </option>
                                );
                            })}
                        </FormSelect>
                    </div>
                ) : null}

                <div className="filterOptions__dateRangeContainer">
                    <div>
                        <label>{labels.orderPlacedAtStartLabel}</label>
                        <div className="filterOptions__dateRangeInputContainer">
                            <DateTime
                                locale={moment.locale()}
                                inputProps={{ style: { height: '38px', width: '138px' } }}
                                input={true}
                                onChange={(newDate) => {
                                    onOrderPlacedAtStartChange(
                                        typeof newDate !== 'string' ? newDate.toDate() : null
                                    );
                                }}
                                timeFormat={false}
                                value={orderPlacedAtStart}
                                isValidDate={validateStartDate}
                                closeOnSelect={true}
                            />
                            <i className="filterOptions__dateRangeIcon material-icons">
                                date_range
                            </i>
                        </div>
                    </div>
                    <div className="filterOptions__lastDateInputContainer">
                        <label>{labels.orderPlacedAtEndLabel}</label>
                        <div className="filterOptions__dateRangeInputContainer">
                            <DateTime
                                locale={moment.locale()}
                                inputProps={{ style: { height: '38px', width: '138px' } }}
                                input={true}
                                onChange={(newDate) => {
                                    onOrderPlacedAtEndChange(
                                        typeof newDate !== 'string' ? newDate.toDate() : null
                                    );
                                }}
                                timeFormat={false}
                                value={orderPlacedAtEnd}
                                isValidDate={validateEndDate}
                                closeOnSelect={true}
                            />
                            <i className="filterOptions__dateRangeIcon material-icons">
                                date_range
                            </i>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function TranslatedFilterOptions(props: FilterOptionsProps) {
    const labels = {
        title: Translate('orders/title/orders'),
        orderTypeLabel: Translate('orders/dropdown/title/order-type'),
        defaultSelectedOrderType: Translate('orders/dropdown/order-type/default'),
        searchLabel: Translate('orders/dropdown/title/filter'),
        brokerAgentLabel: Translate('orders/dropdown/title/broker-agent'),
        orderPlacedAtStartLabel: Translate('orders/list/broker-agent/filter/start-date'),
        orderPlacedAtEndLabel: Translate('orders/list/broker-agent/filter/end-date')
    };

    const dynamicLabels = {
        orderTypeOption: (translationKey: string): string => Translate(translationKey)
    };

    return <FilterOptions {...props} labels={labels} dynamicLabels={dynamicLabels} />;
}

export default TranslatedFilterOptions;
